import React, { useState, useEffect, useCallback, useRef } from 'react';
import useSound from 'use-sound';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useTranslation } from 'react-i18next';
import styles from './App.module.css';
import { buildStorageUrl } from './config/firebaseStorage';
import { useUser } from './context/UserContext';
import { db } from './firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import LaunchPage from './pages/LaunchPage';
import LegalPage from './pages/LegalPage';
import LegalUpdatePopup from './components/LegalUpdatePopup';
import BrowsePage from './pages/BrowsePage';
import CommunityPage from './pages/CommunityPage';
import ProfilePage from './pages/ProfilePage';
import GearPage from './pages/GearPage';
import GearDetailsPage from './pages/GearDetailsPage';
import GearComparePage from './pages/GearComparePage';
import CertificationsPage from './pages/CertificationsPage';
import HowToChoosePage from './pages/HowToChoosePage';
import ContactPage from './pages/ContactPage';
import VerifySuccess from './pages/VerifySuccess';
import AdminPage from './pages/AdminPage';
import SearchResultsPage from './components/SearchResultsPage';
import UploadPage from './pages/UploadPage';
import EditProfilePage from './pages/EditProfilePage';
import MountainDetailsPage from './pages/MountainPage';
import EditMountainPage from './pages/EditMountainPage';
import RoutePage from './pages/RoutePage';
import RoutesPage from './pages/RoutesPage';
import PostDetailsPage from './pages/PostDetailsPage';
import ChatsPage from './pages/ChatsPage';
import FeedbackPageComp from './pages/FeedbackPage';
import LoginPopup from './components/LoginPopup';
import SignupPopup from './components/SignupPopup';
import CookieBanner from './components/CookieBanner';
import API_BASE_URL from './config/api';
import LanguageSelector from './components/LanguageSelector';

import { Helmet } from './utils/helmet';

const ChatView = React.lazy(() => import('./pages/ChatView'));

function AppContent() {
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [mountains, setMountains] = useState([]);
  const [mountainsLoading, setMountainsLoading] = useState(true);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileMenuMounted, setIsMobileMenuMounted] = useState(false);
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  const profileMenuRef = useRef(null);
  const searchInputRef = useRef(null);
  const mobileMenuCloseTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const { currentUser, logout, initializing } = useUser();

  const { t } = useTranslation();

  // Unread messages badge logic
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [playNotification] = useSound('https://assets.mixkit.co/active_storage/sfx/2358/2358-preview.mp3', { volume: 0.4 });
  const lastUnreadTimeRef = useRef(0);
  const isFirstLoadRef = useRef(true);

  useEffect(() => {
    console.log('[UnreadBadge] Effect run. currentUser:', currentUser);
    if (!currentUser) {
      setHasUnreadMessages(false);
      isFirstLoadRef.current = true;
      lastUnreadTimeRef.current = 0;
      return;
    }
    // Listen to chats for unread messages using Firestore db instance
    const q = query(
      collection(db, 'chats'),
      where('participants', 'array-contains', currentUser.uid)
    );
    const unsub = onSnapshot(q, (snap) => {
      let foundUnread = false;
      let maxUnreadTime = 0;
      snap.forEach(doc => {
        const data = doc.data();
        if (data.deletedBy && data.deletedBy[currentUser.uid]) return;
        // Debug output for each chat
        const lastRead = data.lastReadBy && data.lastReadBy[currentUser.uid];
        const updatedAtSec = data.updatedAt && data.updatedAt.seconds;
        const lastReadSec = lastRead && lastRead.seconds;
        console.log('[UnreadBadge] Chat:', {
          id: doc.id,
          lastMessage: data.lastMessage,
          lastMessageSentBy: data.lastMessageSentBy,
          updatedAt: data.updatedAt,
          updatedAtSec,
          lastReadBy: data.lastReadBy,
          lastRead,
          lastReadSec,
          currentUser: currentUser.uid
        });
        if (
          data.lastMessage &&
          data.lastMessageSentBy !== currentUser.uid &&
          (
            !data.lastReadBy ||
            !data.lastReadBy[currentUser.uid] ||
            (
              data.updatedAt &&
              (
                (updatedAtSec && lastReadSec && updatedAtSec > lastReadSec) ||
                (typeof lastRead === 'number' && updatedAtSec > lastRead)
              )
            )
          )
        ) {
          foundUnread = true;
          const time = data.updatedAt?.seconds || 0;
          if (time > maxUnreadTime) maxUnreadTime = time;
          console.log('[UnreadBadge] Unread detected for chat:', doc.id);
        }
      });

      if (!isFirstLoadRef.current && foundUnread && maxUnreadTime > lastUnreadTimeRef.current) {
        playNotification();
      }
      lastUnreadTimeRef.current = maxUnreadTime;
      isFirstLoadRef.current = false;

      setHasUnreadMessages(foundUnread);
      if (!foundUnread) {
        console.log('[UnreadBadge] No unread chats detected.');
      }
    }, (err) => {
      console.error('[UnreadBadge] Firestore error:', err);
    });

    return () => {
      if (typeof unsub === 'function') {
        try {
          unsub();
        } catch (e) {
          console.warn('[UnreadBadge] Cleanup error:', e);
        }
      }
    };
  }, [currentUser, playNotification]);

  const isAdmin = currentUser?.role === 'admin';
  const isEditorOrAdmin = currentUser?.role === 'editor' || isAdmin;

  const isLaunchRoute = location.pathname === '/';

  const MOBILE_DRAWER_ANIMATION_MS = 180;

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
    setIsSearchExpanded(false);

    if (mobileMenuCloseTimeoutRef.current) {
      clearTimeout(mobileMenuCloseTimeoutRef.current);
    }
    mobileMenuCloseTimeoutRef.current = setTimeout(() => {
      setIsMobileMenuMounted(false);
    }, MOBILE_DRAWER_ANIMATION_MS);
  }, []);

  const openMobileMenu = useCallback(() => {
    if (mobileMenuCloseTimeoutRef.current) {
      clearTimeout(mobileMenuCloseTimeoutRef.current);
      mobileMenuCloseTimeoutRef.current = null;
    }

    setIsMobileMenuMounted(true);
    requestAnimationFrame(() => {
      setIsMobileMenuOpen(true);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (mobileMenuCloseTimeoutRef.current) {
        clearTimeout(mobileMenuCloseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isProfileMenuOpen && profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileMenuOpen, profileMenuRef]);

  useEffect(() => {
    if (isSearchExpanded && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchExpanded]);

  useEffect(() => {
    const isAnyPopupOpen = showLogin || showSignup || isMobileMenuMounted;
    if (isAnyPopupOpen) {
      document.body.classList.add(styles.noScroll);
    } else {
      document.body.classList.remove(styles.noScroll);
    }
    return () => {
      document.body.classList.remove(styles.noScroll);
    };
  }, [showLogin, showSignup, isMobileMenuMounted]);

  useEffect(() => {
    const fetchMountains = async () => {
      try {
        setMountainsLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/mountains`);
        const data = await response.json();
        if (data.success) {
          setMountains(data.data);
        } else {
          console.error('Error fetching mountains:', data.message);
          setMountains([]);
        }
      } catch (error) {
        console.error('Error fetching mountains:', error);
        setMountains([]);
      } finally {
        setMountainsLoading(false);
      }
    };
    fetchMountains();
  }, []);

  const openLogin = () => setShowLogin(true);
  const openSignup = () => setShowSignup(true);
  const closeLogin = () => setShowLogin(false);
  const closeSignup = () => setShowSignup(false);

  const switchToSignup = () => {
    setShowLogin(false);
    setShowSignup(true);
  };

  const switchToLogin = () => {
    setShowSignup(false);
    setShowLogin(true);
  };

  const handleMountainUploadSuccess = (newMountain) => {
    setMountains(prevMountains => [...prevMountains, newMountain]);
    navigate('/browse');
  };

  const openProfileMenu = () => {
    setIsProfileMenuOpen(true);
  };

  const closeProfileMenu = () => {
    setIsProfileMenuOpen(false);
  };

  useEffect(() => {
    if (location.pathname === '/search') {
      const params = new URLSearchParams(location.search);
      setGlobalSearchTerm(params.get('q') || '');
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!location.pathname.startsWith('/search')) {
      setIsSearchExpanded(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname, closeMobileMenu]);

  useEffect(() => {
    if (!isMobileMenuMounted) return;
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeMobileMenu();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileMenuMounted, closeMobileMenu]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const trimmed = globalSearchTerm.trim();
    if (!trimmed) return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    setIsSearchExpanded(false);
  };

  const toggleGlobalSearch = () => {
    setIsSearchExpanded((prev) => !prev);
  };

  const closeGlobalSearch = () => {
    setIsSearchExpanded(false);
    setGlobalSearchTerm('');
  };

  const handleMobileLogout = async () => {
    await handleLogout();
    closeMobileMenu();
  };

  const handleLogout = async () => {
    await logout();
    closeProfileMenu();
    navigate('/');
  };

  const handleOpenPublicProfilePage = useCallback((username) => {
    navigate(`/profile/${username}`);
  }, [navigate]);

  /*-------------------------------------- RETURN STATEMENT ---------------------------------------------- */

  return (
    <>
      <Helmet>
        <link rel="icon" type="image/svg+xml" href={`${buildStorageUrl('other/newLogo.svg')}?v=current`} />
      </Helmet>
      <div className={styles.app}>
        {!isLaunchRoute && (
        <header className={styles.appHeader}>
          <div className={styles.headerLeft}>
            <Link to="/" className={styles.homeLink}>
              <div
                className={styles.logo}
                style={{
                  backgroundColor: 'var(--text-color)',
                  WebkitMask: `url(${buildStorageUrl('other/newLogo.svg')}) no-repeat center / contain`,
                  mask: `url(${buildStorageUrl('other/newLogo.svg')}) no-repeat center / contain`
                }}
                role="img"
                aria-label="Logo"
              />
              <h1 className={styles.title}>{t('common.siteName')}</h1>
            </Link>
            {/*<p className={styles.subtitle}>v2</p>*/}
          </div>
          <div className={styles.headerRight}>
            <button
              type="button"
              className={styles.mobileMenuToggle}
              onClick={() => (isMobileMenuOpen ? closeMobileMenu() : openMobileMenu())}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-nav-drawer"
            >
              <span style={{ position: 'relative', display: 'inline-block' }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className={styles.mobileMenuIcon}
                  aria-hidden="true"
                >
                  <path
                    fill="currentColor"
                    d="M3.75 6.75A.75.75 0 0 1 4.5 6h15a.75.75 0 0 1 0 1.5h-15a.75.75 0 0 1-.75-.75Zm0 5.25A.75.75 0 0 1 4.5 11.25h15a.75.75 0 0 1 0 1.5h-15a.75.75 0 0 1-.75-.75Zm0 5.25A.75.75 0 0 1 4.5 16.5h15a.75.75 0 0 1 0 1.5h-15a.75.75 0 0 1-.75-.75Z"
                  />
                </svg>
                {hasUnreadMessages && (
                  <span className={styles.unreadDot} style={{ top: 0, right: 0, left: 'auto', bottom: 'auto' }} aria-label="Unread messages" />
                )}
              </span>
            </button>
            <nav className={styles.navigation}>
              <div className={styles.headerSearch}>
                <button
                  type="button"
                  className={`${styles.searchToggle} ${isSearchExpanded ? styles.searchToggleActive : ''}`}
                  onClick={toggleGlobalSearch}
                  aria-label={isSearchExpanded ? t('nav.searchClose') : t('nav.searchOpen')}
                  aria-expanded={isSearchExpanded}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    className={styles.searchIcon}
                    aria-hidden="true"
                  >
                    <path
                      fill="currentColor"
                      d="M10.5 3a7.5 7.5 0 0 1 5.932 12.11l4.229 4.229a1.25 1.25 0 0 1-1.768 1.768l-4.229-4.229A7.5 7.5 0 1 1 10.5 3m0 2.5a5 5 0 1 0 0 10a5 5 0 0 0 0-10"
                    />
                  </svg>
                </button>
                <form
                  className={`${styles.searchForm} ${isSearchExpanded ? styles.searchFormOpen : ''}`}
                  onSubmit={handleSearchSubmit}
                >
                  <input
                    ref={searchInputRef}
                    type="text"
                    className={styles.searchField}
                    placeholder={t('nav.searchPlaceholder')}
                    value={globalSearchTerm}
                    onChange={(event) => setGlobalSearchTerm(event.target.value)}
                  />
                  <button type="submit" className={styles.searchSubmit}>
                    {t('action.go')}
                  </button>
                  <button
                    type="button"
                    className={styles.searchClose}
                    onClick={closeGlobalSearch}
                    aria-label={t('nav.searchClose')}
                  >
                    ×
                  </button>
                </form>
              </div>
              <div className={styles.navLinks}>
                <Link to="/browse" className={styles.navLink}>{t('nav.browse')}</Link>
                <Link to="/routes" className={styles.navLink}>{t('nav.routes', 'Routes')}</Link>
                <Link to="/gear" className={styles.navLink}>{t('nav.gear')}</Link>
                <Link to="/community" className={styles.navLink}>{t('nav.community')}</Link>
                {currentUser && (
                  <Link to="/chats" className={styles.chatsLink} aria-label={t('nav.chats', 'Chats')}>
                    <span className={styles.chatsIconWrapper}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className={styles.chatsIcon}
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
                      </svg>
                      {hasUnreadMessages && (
                        <span className={styles.unreadDot} aria-label="Unread messages" />
                      )}
                    </span>
                  </Link>
                )}
                {isAdmin && <Link to="/admin" className={styles.navLink}>{t('nav.admin')}</Link>}
              </div>
              <LanguageSelector className={styles.languageSelector} />
            </nav>

            {isMobileMenuMounted && (
              <div
                className={`${styles.mobileDrawerOverlay} ${isMobileMenuOpen ? styles.mobileDrawerOverlayOpen : styles.mobileDrawerOverlayClosed}`}
                onClick={closeMobileMenu}
                role="presentation"
              >
                <aside
                  id="mobile-nav-drawer"
                  className={`${styles.mobileDrawer} ${isMobileMenuOpen ? styles.mobileDrawerOpen : styles.mobileDrawerClosed}`}
                  role="dialog"
                  aria-modal="true"
                  aria-label="Menu"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className={styles.mobileDrawerHeader} />

                  <div className={styles.mobileDrawerBody}>
                    <div className={styles.mobileDrawerSearch}>
                      <div className={styles.headerSearch}>
                        <button
                          type="button"
                          className={`${styles.searchToggle} ${isSearchExpanded ? styles.searchToggleActive : ''}`}
                          onClick={toggleGlobalSearch}
                          aria-label={isSearchExpanded ? t('nav.searchClose') : t('nav.searchOpen')}
                          aria-expanded={isSearchExpanded}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            className={styles.searchIcon}
                            aria-hidden="true"
                          >
                            <path
                              fill="currentColor"
                              d="M10.5 3a7.5 7.5 0 0 1 5.932 12.11l4.229 4.229a1.25 1.25 0 0 1-1.768 1.768l-4.229-4.229A7.5 7.5 0 1 1 10.5 3m0 2.5a5 5 0 1 0 0 10a5 5 0 0 0 0-10"
                            />
                          </svg>
                        </button>
                        <form
                          className={`${styles.searchForm} ${isSearchExpanded ? styles.searchFormOpen : ''}`}
                          onSubmit={(event) => {
                            handleSearchSubmit(event);
                            closeMobileMenu();
                          }}
                        >
                          <input
                            ref={searchInputRef}
                            type="text"
                            className={styles.searchField}
                            placeholder={t('nav.searchPlaceholder')}
                            value={globalSearchTerm}
                            onChange={(event) => setGlobalSearchTerm(event.target.value)}
                          />
                          <button type="submit" className={styles.searchSubmit}>
                            {t('action.go')}
                          </button>
                          <button
                            type="button"
                            className={styles.searchClose}
                            onClick={closeGlobalSearch}
                            aria-label={t('nav.searchClose')}
                          >
                            ×
                          </button>
                        </form>
                      </div>
                    </div>

                    <div className={styles.mobileDrawerLinks}>
                      <Link to="/browse" className={styles.mobileNavLink} onClick={closeMobileMenu}>
                        {t('nav.browse')}
                      </Link>
                      <Link to="/routes" className={styles.mobileNavLink} onClick={closeMobileMenu}>
                        {t('nav.routes', 'Routes')}
                      </Link>
                      <Link to="/gear" className={styles.mobileNavLink} onClick={closeMobileMenu}>
                        {t('nav.gear')}
                      </Link>
                      <Link to="/community" className={styles.mobileNavLink} onClick={closeMobileMenu}>
                        {t('nav.community')}
                      </Link>
                      {currentUser && (
                        <Link to="/chats" className={styles.mobileNavLink} onClick={closeMobileMenu} style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                          <span style={{ width: '100%', textAlign: 'center', position: 'relative', display: 'inline-block' }}>
                            {t('nav.chats', 'Chats')}
                            {hasUnreadMessages && (
                              <span className={styles.unreadDot} style={{ top: 2, right: 2, left: 'auto', bottom: 'auto', position: 'absolute' }} aria-label="Unread messages" />
                            )}
                          </span>
                        </Link>
                      )}
                      {isAdmin && (
                        <Link to="/admin" className={styles.mobileNavLink} onClick={closeMobileMenu}>
                          {t('nav.admin')}
                        </Link>
                      )}
                    </div>

                    <div className={styles.mobileDrawerUser}>
                      {initializing ? (
                        <div className={styles.authButtons}>
                          <span style={{ color: 'var(--text-color)' }}>{t('common.loading')}</span>
                        </div>
                      ) : currentUser ? (
                        <div className={styles.mobileDrawerUserRow}>
                          <Link
                            to="/settings"
                            onClick={closeMobileMenu}
                            className={styles.mobileDrawerPfpLink}
                            aria-label={t('common.settings', { defaultValue: 'Settings' })}
                          >
                            <span className={styles.pfpButton} role="img" aria-label={t('common.settings', { defaultValue: 'Settings' })}>
                              <img
                                src={
                                  currentUser.pfp && currentUser.pfp.trim() !== ""
                                    ? currentUser.pfp
                                    : buildStorageUrl('pfp/placeholderP.png')
                                }
                                alt={`${currentUser.nickname}'s Profile`}
                                className={styles.pfpImage}
                              />
                            </span>
                          </Link>
                          <div className={styles.mobileDrawerUserActions}>
                            <Link to="/settings" onClick={closeMobileMenu} className={styles.mobileDrawerUserLink}>
                              {t('common.settings', { defaultValue: 'Settings' })}
                            </Link>
                            <button onClick={handleMobileLogout} className={styles.mobileDrawerLogoutButton}>
                              {t('common.logout')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className={styles.authButtons}>
                          <button
                            onClick={() => {
                              closeMobileMenu();
                              openLogin();
                            }}
                            className={styles.loginBtn}
                          >
                            {t('action.login')}
                          </button>
                          <button
                            onClick={() => {
                              closeMobileMenu();
                              openSignup();
                            }}
                            className={styles.signupBtn}
                          >
                            {t('action.signup')}
                          </button>
                        </div>
                      )}
                    </div>

                    <div style={{
                      marginTop: '0.5rem',
                      marginBottom: '0.5rem',
                      display: 'flex',
                      justifyContent: 'center',
                      opacity: 0.4
                    }}>
                      <div
                        style={{
                          width: '100%',
                          height: '180px',
                          backgroundColor: 'var(--text-color)',
                          WebkitMask: `url(${buildStorageUrl('other/newLogo.svg')}) no-repeat center / contain`,
                          mask: `url(${buildStorageUrl('other/newLogo.svg')}) no-repeat center / contain`
                        }}
                        role="img"
                        aria-label="Logo"
                      />
                    </div>

                    <div className={styles.mobileDrawerFooter}>
                      <LanguageSelector className={styles.languageSelector} />
                    </div>
                  </div>
                </aside>
              </div>
            )}

            <div className={styles.userInfo}>
              {initializing ? (
                <div className={styles.authButtons}>
                  <span style={{ color: 'var(--text-color)' }}>{t('common.loading')}</span>
                </div>
              ) : currentUser ? (
                <div className={styles.loggedInUserInfo} ref={profileMenuRef} style={{ position: 'relative' }}>
                  <button onClick={openProfileMenu} className={styles.pfpButton}>
                    <img
                      src={
                        currentUser.pfp && currentUser.pfp.trim() !== ""
                          ? currentUser.pfp
                          : buildStorageUrl('pfp/placeholderP.png')
                      }
                      alt={`${currentUser.nickname}'s Profile`}
                      className={styles.pfpImage}
                    />
                  </button>
                  {isProfileMenuOpen && (
                    <div className={styles.launchProfileMenu}>
                      <Link to="/profile" onClick={closeProfileMenu} className={styles.lightLink}>
                        {t('common.profile')}
                      </Link>
                      <Link to="/settings" onClick={closeProfileMenu} className={styles.lightLink}>
                        {t('common.settings', { defaultValue: 'Settings' })}
                      </Link>
                      <button onClick={handleLogout} className={styles.profileMenuButton}>
                        {t('common.logout')}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className={styles.authButtons}>
                  <button onClick={openLogin} className={styles.loginBtn}>
                    {t('action.login')}
                  </button>
                  <button onClick={openSignup} className={styles.signupBtn}>
                    {t('action.signup')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
      )}

      {isLaunchRoute && (
        <div style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 10000 }}>
          {/*--------------------------------------- LAUNCH SCREEN --------------------------------------------- */}
          {initializing ? (
            <div className={styles.authButtons}>
              <span style={{ color: 'var(--bg-color)' }}>{t('common.loading')}</span>
            </div>
          ) : currentUser ? (
            <div ref={profileMenuRef} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <button onClick={openProfileMenu} className={styles.pfpButton}>
                <img
                  src={
                    currentUser.pfp && currentUser.pfp.trim() !== ""
                      ? currentUser.pfp
                      : buildStorageUrl('pfp/placeholderP.png')
                  }
                  alt={`${currentUser.nickname}'s Profile`}
                  className={styles.pfpImage}
                />
              </button>
              {isProfileMenuOpen && (
                <div className={styles.launchProfileMenu}>
                  <Link to="/profile" onClick={closeProfileMenu} className={styles.lightLink}>
                    {t('common.profile')}
                  </Link>
                  <Link to="/settings" onClick={closeProfileMenu} className={styles.lightLink}>
                    {t('common.settings', { defaultValue: 'Settings' })}
                  </Link>
                  <Link to="/chats" onClick={closeProfileMenu} className={styles.lightLink}>
                    {t('nav.chats', 'Chats')}
                  </Link>
                  <button onClick={handleLogout} className={styles.profileMenuButton}>
                    {t('common.logout')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className={styles.authButtons}>
              <button onClick={openLogin} className={styles.loginBtn}>
                {t('action.login')}
              </button>
              <button onClick={openSignup} className={styles.signupBtn}>
                {t('action.signup')}
              </button>
            </div>
          )}
        </div>
      )}

      <main className={styles.mainContent} style={isLaunchRoute ? { padding: 0 } : undefined}>
        {/* ---------------------------------- MAIN ----------------- ROUTES ------------------------ */}
        <TransitionGroup component={null}>
          <CSSTransition key={location.key} classNames="page-fade" timeout={300}>
            <Routes location={location}>
          <Route path="/" element={<LaunchPage onOpenLogin={openLogin} onOpenSignup={openSignup} />} />
          <Route path="/verify-success" element={<VerifySuccess />} />
          <Route path="/browse" element={<BrowsePage mountains={mountains} loading={mountainsLoading} onOpenLogin={openLogin} onUploadSuccess={handleMountainUploadSuccess} />} />
          <Route path="/routes" element={<RoutesPage mountains={mountains} />} />
          <Route path="/gear" element={<GearPage onOpenLogin={openLogin} />} />
          <Route path="/gear/compare" element={<GearComparePage />} />
          <Route path="/gear/certifications" element={<CertificationsPage />} />
          <Route path="/community" element={<CommunityPage allMountains={mountains} onOpenPublicProfilePage={handleOpenPublicProfilePage} />} />
          <Route path="/post/:postId" element={<PostDetailsPage />} />
          <Route path="/profile" element={<ProfilePage mountains={mountains} />} />
          <Route path="/profile/:username" element={<ProfilePage mountains={mountains} />} /> {/* For public profiles */}

          {/* Chats / Messaging feature */}
          <Route
            path="/chats"
            element={
              currentUser ? (
                <ChatsPage />
              ) : (
                <div className={styles.contentSection}>
                  <h2>{t('common.status.accessDenied', 'Access Denied')}</h2>
                  <p>{t('common.status.loginRequired', 'Please log in to access your chats.')}</p>
                </div>
              )
            }
          />

          <Route
            path="/chats/:chatId"
            element={
              currentUser ? (
                <React.Suspense fallback={<div>Loading chat...</div>}>
                  <ChatView />
                </React.Suspense>
              ) : (
                <div className={styles.contentSection}>
                  <h2>{t('common.status.accessDenied', 'Access Denied')}</h2>
                  <p>{t('common.status.loginRequired', 'Please log in to access your chats.')}</p>
                </div>
              )
            }
          />

          <Route
            path="/admin"
            element={
              isAdmin ? (
                <AdminPage />
              ) : (
                <div className={styles.contentSection}>
                  <h2>{t('common.status.accessDenied')}</h2>
                  <p>{t('common.status.permissionView')}</p>
                </div>
              )
            }
          />
          <Route
            path="/mountains/:id"
            element={<MountainDetailsPage />}
          />
          <Route path="/routes/:routeId" element={<RoutePage mountains={mountains} />} />
          <Route
            path="/mountains/:id/edit"
            element={
              isEditorOrAdmin ? (
                <EditMountainPage />
              ) : (
                <div className={styles.contentSection}>
                  <h2>{t('common.status.accessDenied')}</h2>
                  <p>{t('common.status.permissionEdit')}</p>
                </div>
              )
            }
          />
          <Route
            path="/upload"
            element={<UploadPage onUploadSuccess={handleMountainUploadSuccess} />}
          />
          <Route
            path="/profile/edit"
            element={<EditProfilePage />}
          />
          <Route
            path="/settings"
            element={<EditProfilePage />}
          />
          <Route
            path="/search"
            element={<SearchResultsPage />}
          />
          <Route path="/gear/guide/:category" element={<HowToChoosePage />} />
          <Route path="/gear/:id" element={<GearDetailsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/feedback" element={<FeedbackPageComp />} />
          <Route path="/privacy" element={<LegalPage forcedType="privacy" />} />
          <Route path="/terms" element={<LegalPage forcedType="terms" />} />
          <Route
            path="*"
            element={
              <div className={styles.contentSection}>
                <h2>Page not found</h2>
                <p>The page you are looking for does not exist.</p>
                <Link to="/" className={styles.goHomeLink}>Go to Home</Link>
              </div>
            }
          />
            </Routes>
          </CSSTransition>
        </TransitionGroup>
      </main>

      <LegalUpdatePopup />
      <CookieBanner />
      {!isLaunchRoute && (
        <footer className={styles.appFooter}>
          {/* -------------------- FOOTER -------------------- */}
          <Link to="/privacy" style={{ color: 'var(--bg-color)' }}>Privacy Policy</Link>
          <span style={{ color: 'var(--bg-color)' }}> • </span>
          <Link to="/terms" style={{ color: 'var(--bg-color)' }}>Terms of Service</Link>
          <span style={{ color: 'var(--bg-color)' }}> • </span>
          <Link to="/contact" style={{ color: 'var(--bg-color)' }}>Contact</Link>
          <span style={{ color: 'var(--bg-color)' }}> • </span>
          <Link to="/feedback" style={{ color: 'var(--bg-color)' }}>Anonymous Feedback</Link>
          <span className={styles.desktopOnly} style={{ color: 'var(--bg-color)' }}> • </span>
          <a href="https://krisztian.fi" target="_blank" rel="noopener noreferrer" className={styles.desktopOnly} style={{ color: 'var(--bg-color)' }}>About creator</a>
        </footer>
      )}

      <LoginPopup
        isOpen={showLogin}
        onClose={closeLogin}
        onSwitchToSignup={switchToSignup}
      />
      <SignupPopup
        isOpen={showSignup}
        onClose={closeSignup}
        onSwitchToLogin={switchToLogin}
      />
    </div>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
